/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography, Checkbox, IconButton, CircularProgress, TextareaAutosize, Slider } from '@mui/material';
import { FiInfo, FiExternalLink, FiDownload, FiTrash2, FiEdit2, FiAlertCircle } from 'react-icons/fi';
import CheerioButton from '../../../Components/CheerioButton';
import images from '../../../Utils/images';
import UploaderText from '../../../Components/UploaderText';
import UploadValidator from '../../../Components/UploadValidator';
import { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MessageGroundChatBox from '../MessageGroundChatBox';
import { useNavigate } from 'react-router-dom';
import { fetchModels, extractProviders, getModelsForProvider, Model } from './actions';
import { createAIAssistant, CreateAIAssistantPayload, getAIAgentDetails, updateAIAssistant, deleteKnowledgeBaseItem } from '../actions';
import Snackbar from '@mui/material/Snackbar';
import { useSelector } from 'react-redux';
import { eventsend } from '../../../Config/analyticsFunctions';

const currencyConversionRates: Record<string, number> = {
    USD: 1,
    INR: 88
};

function priceAfterMarkup(price: number | string, currency: string = "INR"): number {
    let numericPrice = Number(price);

    if (!numericPrice) numericPrice = 0.00000001;

    const markedUpPrice = numericPrice + numericPrice * 10;

    const conversionRate = currencyConversionRates[currency?.toUpperCase()] ?? 1;
    const convertedPrice = markedUpPrice * conversionRate;

    const rounded = parseFloat(convertedPrice.toFixed(8));
    return rounded;
}

export default function CreateAssistantPage() {
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const { agentId } = useParams<{ agentId?: string }>();
    // Types
    type FileItem = { id?: string; name: string; progress: number; status: 'uploading' | 'done'; type: string; file: File | undefined; kbItemId?: string };
    type LinkItem = { url: string; id?: string };

    const navigate = useNavigate();

    // Models API state
    const [modelsList, setModelsList] = useState<Model[]>([]);
    const [providers, setProviders] = useState<string[]>([]);
    const [provider, setProvider] = useState<string>('openai');
    const [model, setModel] = useState<string>('openai/gpt-4o-mini');
    const [modelsForProvider, setModelsForProvider] = useState<Model[]>([]);
    const [loadingModels, setLoadingModels] = useState<boolean>(false);
    const [providerSearch, setProviderSearch] = useState('');
    const [modelSearch, setModelSearch] = useState('');
    // const [assistantName, setAssistantName] = React.useState('');
    const [instructions, setInstructions] = React.useState('');
    const [isInstructionsModalOpen, setInstructionsModalOpen] = React.useState(false);
    const [isReferencesModalOpen, setReferencesModalOpen] = React.useState(false);
    const [files, setFiles] = React.useState<FileItem[]>([]);
    const [assistantName, setAssistantName] = React.useState('');
    const [fallbackResponse, setFallbackResponse] = React.useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [errors, setErrors] = useState<{
        name?: string;
        provider?: string;
        model?: string;
        instructions?: string;
        files?: string;
        fallbackResponse?: string;
    }>({});
    const [links, setLinks] = React.useState<LinkItem[]>([]);
    const [isLinksModalOpen, setLinksModalOpen] = React.useState(false);
    const [linkInput, setLinkInput] = React.useState('');
    const [editLinkIdx, setEditLinkIdx] = React.useState<number | null>(null);
    const [linkError, setLinkError] = React.useState('');
    const [fileError, setFileError] = React.useState('');
    const [isUploading, setUploading] = React.useState(false);
    // const [responseLimit, setResponseLimit] = React.useState('');
    // const [delayedResponse, setDelayedResponse] = React.useState('');
    // const [fallbackResponse, setFallbackResponse] = React.useState('');
    const [fallbackAction, setFallbackAction] = React.useState('assign_to_agent');
    const [exitAfterTime, setExitAfterTime] = React.useState(false);
    const [exitAfterCount, setExitAfterCount] = React.useState(false);
    const [exitTimeValue, setExitTimeValue] = React.useState('5');
    const [exitCountValue, setExitCountValue] = React.useState('50');
    const [exitResponse, setExitResponse] = React.useState('');
    const [exitAction, setExitAction] = React.useState('assign_to_agent');
    const [humanizeResponse, setHumanizeResponse] = useState(false);
    const [humanizeSpeed, setHumanizeSpeed] = useState('30');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const authtoken = useSelector((state: any) => state.main.auth_token);
    const localdata = useSelector((state: any) => state.main.localdata);

    const currency = localdata?.walletCurrency;
    const currencySymbol = currency == 'INR' ? '₹' : '$';
    const highRateModelLimit = currency === 'INR' ? 1500 : 15;

    // Fetch providers/models on mount
    useEffect(() => {
        setLoadingModels(true);
        fetchModels()
            .then(models => {
                setModelsList(models);
                const provs = extractProviders(models);
                setProviders(provs);
                // set default provider to openai 
                const openaiProvider = provs.find(p => p.trim().toLowerCase() === 'openai');
                setProvider(openaiProvider || '');
            })
            .finally(() => setLoadingModels(false));
    }, []);

    // Fetch agent details if agentId is present (edit mode)
    useEffect(() => {
        if (!agentId || modelsList.length === 0 || providers.length === 0) return;
        (async () => {
            try {
                const data = await getAIAgentDetails(agentId, authtoken);
                console.log("data", data)
                setAssistantName(data.name || '');

                // Provider mapping: match exactly from providers list (case-insensitive)
                const providerFromAPI = (data.provider || '').trim().toLowerCase();
                const matchedProvider = providers.find(p => p.trim().toLowerCase() === providerFromAPI) || data.provider || '';
                setProvider(matchedProvider);

                setTimeout(() => {
                    const modelFromAPI = (data.ai_model || '').trim().toLowerCase();
                    const matchedModel = modelsList.find(m => m.name.trim().toLowerCase() === modelFromAPI) || data.ai_model || '';
                    setModel(matchedModel);
                }, 100);

                setInstructions(data.instructions || '');
                setFallbackResponse(data.fallback_response || '');
                setLinks(Array.isArray(data.knowledgeBaseItems) ? data.knowledgeBaseItems.filter((i: any) => i.type === 'link').map((i: any) => ({ url: i.url, id: i.id || i._id })) : []);
                setFiles(Array.isArray(data.knowledgeBaseItems) ? data.knowledgeBaseItems.filter((i: any) => i.type === 'file').map((i: any) => ({ name: i.title, progress: 100, status: 'done', type: i.fileType, file: undefined, kbItemId: i.id || i._id })) : []);

                setExitAfterTime(data.exit_after_time_in_minutes || false);
                setExitAfterCount(data.exit_after_no_of_responses || false);
                setExitTimeValue(data.exit_after_time_in_minutes || '5');
                setExitCountValue(data.exit_after_no_of_responses || '50');
                setExitResponse(data.exit_response || '');
                setExitAction(data.exit_action || 'assign_to_agent');
                setFallbackAction(data.fallback_action || 'assign_to_agent');
                setHumanizeResponse(data.humanize_response || false);
                setHumanizeSpeed(data.typing_speed || 30);
            } catch (err) {
                setSubmitError('Failed to fetch agent details');
            }
        })();
    }, [agentId, authtoken, modelsList, providers]);

    // Update models list when provider changes
    useEffect(() => {
        if (!provider) return;
        const filtered = getModelsForProvider(modelsList, provider);
        setModelsForProvider(filtered);
        console.log(filtered);
        if (filtered.length > 0) {
            setModel(filtered[0].id);
        } else {
            setModel('');
        }
    }, [provider, modelsList]);

    // File upload handler (multiple files)
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputFiles = e.target.files;
        if (!inputFiles || inputFiles.length === 0) return;
        eventsend('file_uploaded_to_kb');
        let valid = true;
        const newFiles: FileItem[] = [];
        Array.from(inputFiles).forEach(file => {
            if (!UploadValidator(file, 'document')) {
                valid = false;
            } else {
                newFiles.push({ name: file.name, progress: 0, status: 'uploading', type: file.type, file });
            }
        });
        if (!valid) {
            setFileError('One or more files are invalid.');
            return;
        }
        setFileError('');
        setUploading(true);
        setFiles(prev => [...prev, ...newFiles]);
        // Simulate upload progress for each file
        newFiles.forEach(file => {
            let progress = 0;
            const interval = setInterval(() => {
                progress += 20;
                setFiles(prevFiles => prevFiles.map(f => f.name === file.name ? { ...f, progress: Math.min(progress, 100), status: (progress >= 100 ? 'done' : 'uploading') as 'uploading' | 'done' } : f));
                if (progress >= 100) {
                    clearInterval(interval);
                    setUploading(false);
                }
            }, 200);
        });
    };
    const handleFileDelete = async (file: any) => {
        // If file has kbItemId (existing in backend), delete via API
        console.log(file);
        if (file.kbItemId && agentId) {
            try {
                await deleteKnowledgeBaseItem(agentId, file.kbItemId, authtoken);
                setFiles(prev => prev.filter(f => f.kbItemId !== file.kbItemId));
                setSnackbar({ open: true, message: 'File deleted successfully', severity: 'success' });
            } catch (err) {
                setSnackbar({ open: true, message: 'Failed to delete file', severity: 'error' });
            }
        } else {
            setFiles(prev => prev.filter(f => f.name !== file.name));
        }
    };
    // Modal content for instructions
    const InstructionsModal = React.memo(({ open, instructions, setInstructions, onClose, onViewReferences }: {
        open: boolean;
        instructions: string;
        setInstructions: (s: string) => void;
        onClose: () => void;
        onViewReferences: () => void;
    }) => {
        const [localValue, setLocalValue] = React.useState(instructions);
        React.useEffect(() => {
            setLocalValue(instructions);
        }, [instructions, open]);
        if (!open) return null;
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#fff',
                        borderRadius: 3,
                        width: '80vw',
                        height: '80vh',
                        p: 4,
                        boxShadow: 3,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <IconButton sx={{ position: 'absolute', top: 16, right: 16 }} onClick={onClose}>
                        <img src={images.CreatorClose} alt="close" style={{ width: 28, height: 28 }} />
                    </IconButton>

                    <Typography fontWeight={700} fontSize={20} mb={2}>
                        Instructions
                    </Typography>

                    <TextareaAutosize
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        minRows={5}
                        maxRows={5}
                        style={{
                            flexGrow: 1,
                            width: '100%',
                            resize: 'none',
                            padding: '16px',
                            fontSize: '16px',
                            borderRadius: '8px',
                            border: '1px solid #ccc',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            overflowY: 'auto',
                        }}
                        autoFocus
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                        <Button
                            variant="contained"
                            sx={{
                                background: '#373FCC',
                                color: '#fff',
                                fontWeight: 700,
                                borderRadius: 2,
                                px: 4,
                                boxShadow: 'none',
                            }}
                            onClick={() => {
                                setInstructions(localValue);
                                onClose();
                            }}
                        >
                            Save
                        </Button>
                        <Button variant="text" sx={{ color: '#373FCC', ml: 2 }} onClick={onViewReferences}>
                            View references
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    });
    InstructionsModal.displayName = 'InstructionsModal';
    // Reference Instructions Model
    const referenceInstructions = [
        {
            title: 'Ecommerce Sales Assistant',
            description: `Consider yourself as "Riya", an AI sales assistant for a fashion store. Your job is to help customers find the right products, answer questions about size, delivery, and return policy. You should ask questions to understand their needs, recommend products based on their replies, and share purchase links. Be friendly, stylish, and helpful. If something is unclear, politely ask the customer again.`
        },
        {
            title: 'Restaurant Reservation Assistant',
            description: `Consider yourself as "ChefBot", an AI assistant for a restaurant. Your job is to help customers book tables, view the menu, and understand current offers. Ask the customer when they want to visit, how many people are coming, and if they have any preferences. Use a warm and welcoming tone. If you don’t understand a message, ask again clearly.`
        },
        {
            title: 'Service Booking Assistant',
            description: `Consider yourself as "Fixi", an AI booking assistant for a home services business. Your job is to help customers book appointments, explain services offered, and check location & timing preferences. Always ask clear follow-up questions to confirm the details. Be polite and to-the-point. If confused, ask the customer to repeat.`
        },
        {
            title: 'Education Counselor Assistant',
            description: `Consider yourself as "EduBot", an AI counselor for a coaching institute. Your job is to guide students/parents about courses, batch timings, and fees. Ask about the student’s grade, subjects of interest, and learning goals before suggesting anything. Maintain an encouraging and respectful tone. Clarify if any detail is missing.`
        },
        {
            title: 'Beauty Salon Assistant',
            description: `Consider yourself as "Glow", an AI assistant for a salon. Your job is to assist customers in booking services, checking stylist availability, and informing about offers. Ask which service they want (haircut, facial, etc.), date/time, and preferred stylist. Be friendly and graceful in tone. Reconfirm anything unclear.`
        }
    ];

    // Modal content for references (with insert/copy functionality)
    const ReferencesModal = () => (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, width: "80vw", maxHeight: '85vh', p: 4, boxShadow: 3, position: 'relative', overflowY: 'auto' }}>
                <IconButton sx={{ position: 'absolute', top: 16, right: 16 }} onClick={() => setReferencesModalOpen(false)}>
                    <img src={images.CreatorClose} alt="close" style={{ width: 28, height: 28 }} />
                </IconButton>
                <Typography fontWeight={700} fontSize={20} mb={2}>Reference Instructions</Typography>
                <Typography fontSize={15} color="#666" mb={3}>Select a template to use as a starting point for your assistant’s instructions. Click “Use” to insert it below.</Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    {referenceInstructions.map((ref, idx) => (
                        <Box key={idx} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2.5, background: '#fafbfc', position: 'relative' }}>
                            <Typography fontWeight={700} fontSize={16} mb={1}>{ref.title}</Typography>
                            <Typography fontSize={14} color="#444" mb={2}>{ref.description}</Typography>
                            <Button
                                size="small"
                                variant="contained"
                                sx={{ background: '#373FCC', color: '#fff', borderRadius: 2, fontWeight: 700, fontSize: 13, px: 3, position: 'absolute', top: 16, right: 16 }}
                                onClick={() => {
                                    setInstructions(ref.description);
                                    setReferencesModalOpen(false);
                                }}
                            >
                                Use
                            </Button>
                        </Box>
                    ))}
                </Box>
                <Box mt={4} display="flex" justifyContent="flex-end">
                    <Button variant="outlined" sx={{ borderColor: '#373FCC', color: '#373FCC', borderRadius: 2, px: 4, fontWeight: 700 }} onClick={() => setReferencesModalOpen(false)}>Close</Button>
                </Box>
            </Box>
        </Box>
    );

    // Form validation
    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!assistantName.trim()) newErrors.name = 'Name is required';
        if (!provider) newErrors.provider = 'Provider is required';
        if (!model) newErrors.model = 'Model is required';
        if (!instructions.trim()) newErrors.instructions = 'Instructions are required';
        if (files.length === 0) newErrors.files = 'At least one file is required';
        if (!fallbackResponse.trim()) newErrors.fallbackResponse = 'Fallback response is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit handler
    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (submitLoading) return;

        if (!validateForm()) {
            eventsend('assistant_creation_failed');
            setSubmitError('Please fill in all required fields');
            return;
        }

        setSubmitLoading(true);
        setSubmitError('');

        try {
            // Only send files that are in 'done' status
            const fileObjs = files.filter(f => f.status === 'done');

            // Create base payload with only the fields we want to update
            const payload: Partial<CreateAIAssistantPayload> = {
                name: assistantName,
                provider,
                ai_model: model,
                instructions,
                fallback_response: fallbackResponse,
                exit_after_time_in_minutes: exitAfterTime ? parseInt(exitTimeValue) : undefined,
                exit_after_no_of_responses: exitAfterCount ? parseInt(exitCountValue) : undefined,
                exit_response: exitResponse,
                exit_action: exitAction,
                fallback_action: fallbackAction,
                humanize_response: humanizeResponse,
                typing_speed: parseFloat(humanizeSpeed),
            };

            // Always include knowledgeBaseItems as they need to be synced, but exclude items that already have an ID
            payload.knowledgeBaseItems = [
                ...fileObjs
                    .filter(f => !f.kbItemId) // Exclude files that already have an ID
                    .map(f => ({ type: 'file', title: f.name, fileType: f.type.split('/').pop() || 'txt' })),
                ...links
                    .filter(l => !l.id) // Exclude links that already have an ID
                    .map(l => ({ type: 'link', url: l.url }))
            ];

            // Only include files in create mode or if there are new files in edit mode
            if (!agentId || fileObjs.some(f => f.file)) {
                payload.files = fileObjs.map(f => f.file).filter((f): f is File => !!f);
            }

            if (agentId) {
                await updateAIAssistant(agentId, { ...payload, authToken: authtoken } as CreateAIAssistantPayload, payload.files || []);
                setSnackbar({ open: true, message: 'Assistant updated successfully', severity: 'success' });
            } else {
                const res = await createAIAssistant({ ...payload, authToken: authtoken } as CreateAIAssistantPayload);
                eventsend('assistant_created');
                // Try to get new agent id from response
                const newId = res?.data?.data?.id || res?.data?.data?._id;
                if (newId) {
                    navigate(`/creator/ai-assistants/edit/${newId}`);
                } else {
                    navigate('/creator/ai-assistants');
                }
            }
        } catch (err: any) {
            eventsend('assistant_creation_failed', {
                error_type: err?.response?.data?.message || 'unknown_error',
                step: 'submission'
            });
            setSubmitError(err?.response?.data?.message || 'Failed to save assistant');
            setSnackbar({ open: true, message: 'Failed to save assistant', severity: 'error' });
        } finally {
            setSubmitLoading(false);
        }
    };


    return (
        <>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />
            <Box sx={{ display: 'flex', height: '100vh', width: '100%', background: '#fafbfc', overflow: 'hidden' }}>
                {/* Left Panel: Form (scrollable) */}
                <Box sx={{ width: 400, background: '#fff', p: 1, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', height: '100vh', position: 'relative', zIndex: 2 }}>
                    <Button variant="text" sx={{ mb: 1.5, textTransform: 'none', color: '#222', fontWeight: 700, fontSize: 18, justifyContent: 'flex-start', pl: 0 }} startIcon={<img src={images.BackArrowBlack} alt="Back" style={{ width: 22, height: 22 }} />} onClick={() => navigate('/creator/ai-assistants')}>
                        {
                            agentId ? 'Edit Assistant' : 'Create Assistant'
                        }
                    </Button>
                    <Typography fontWeight={700} fontSize={14}>Name Assistant</Typography>
                    <TextField
                        fullWidth
                        label="Assistant Name"
                        value={assistantName}
                        onChange={(e) => {
                            setAssistantName(e.target.value);
                            eventsend('assistant_name_added', {
                                input_added: e.target.value,
                                input_character_count: e.target.value.length
                            });
                            if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                        required
                        size="small"
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.provider}>
                        <InputLabel id="provider-select-label">Provider</InputLabel>
                        <Select
                            labelId="provider-select-label"
                            value={provider}
                            label="Provider"
                            onChange={e => {
                                setProvider(e.target.value);
                                eventsend('provider_selected', {
                                    provider_selected: e.target.value,
                                });
                                if (errors.provider) setErrors(prev => ({ ...prev, provider: '' }));
                                eventsend("provider_selected");
                            }}
                            displayEmpty
                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                        >
                            {providers.map(p => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.model}>
                        <InputLabel id="model-select-label">Model</InputLabel>
                        <Select
                            labelId="model-select-label"
                            value={model}
                            label="Model"
                            onChange={e => {
                                setModel(e.target.value);
                                eventsend('model_selected', {
                                    model_selected: e.target.value,
                                    previously_used_model: model,
                                });
                                if (errors.model) setErrors(prev => ({ ...prev, model: '' }));
                            }}
                            displayEmpty
                            disabled={modelsForProvider.length === 0}
                            MenuProps={{ PaperProps: { style: { maxHeight: 350 } } }}
                            renderValue={(selected: unknown) => (typeof selected === 'string' && selected) || 'Select model'}
                        >
                            {modelsForProvider.map(m => {
                                let inputPrice = 'N/A';
                                let outputPrice = 'N/A';
                                if (m.pricing) {
                                    if (m.pricing.prompt) inputPrice = `${currencySymbol}${(priceAfterMarkup(m.pricing.prompt, currency) * 1000000).toFixed(2)}/M input tokens`;
                                    if (m.pricing.completion) outputPrice = `${currencySymbol}${(priceAfterMarkup(m.pricing.completion, currency) * 1000000).toFixed(2)}/M output tokens`;
                                }
                                return (
                                    <MenuItem key={m.id} value={m.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
                                        <Box>{(m.name.split(':').slice(1).join(':').trim() || m.name).replace('(free)', '')}</Box>
                                        <Box sx={{ fontSize: 12, color: '#888', fontWeight: 500 }}>
                                            {inputPrice} <span style={{ margin: '0 8px' }}>|</span> {outputPrice}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Box display="flex" alignItems="center">
                        <Typography fontSize={14} color="#888">Model Cost</Typography>
                        <Typography fontSize={14} ml={1} color="#444" fontWeight={600}>
                            {
                                modelsForProvider.find(m => m.id === model)?.pricing?.prompt &&
                                `${currencySymbol}${(priceAfterMarkup(modelsForProvider.find(m => m.id === model)?.pricing?.prompt || 0, currency) * 1000000).toFixed(2)}/M input | ${currencySymbol}${(priceAfterMarkup(modelsForProvider.find(m => m.id === model)?.pricing?.completion || 0, currency) * 1000000).toFixed(2)}/M output`
                            }
                        </Typography>


                        <Tooltip
                            title="AI Tokens represent the amount of content processed or generated by the AI in a conversation. Each token roughly equals a word fragment or short word. Managing token usage helps you control cost, response length, and performance—especially for longer chats or high-volume automations."
                            arrow
                        >
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                <FiInfo />
                            </Box>
                        </Tooltip>
                    </Box>
                    {
                        Number((priceAfterMarkup(modelsForProvider.find(m => m.id === model)?.pricing?.prompt || 0, currency) * 1000000).toFixed(2)) > highRateModelLimit && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#f3e8ff', // light purple
                                    border: '1px solid #373FCC',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1.5,
                                    mt: 2,
                                }}
                            >
                                <FiAlertCircle style={{ color: '#373FCC', marginRight: 8 }} size={20} />
                                <Typography fontSize={14} color="#333">
                                    <strong style={{ color: '#373FCC' }}>High Cost:</strong> This model may incur higher usage charges.
                                </Typography>
                            </Box>
                        )
                    }

                    <Box display="flex" alignItems="center" mt={2} mb={0.5}>
                        <Typography fontWeight={700} fontSize={14}>Instructions</Typography>
                        <Tooltip title="Instructions help personalize your AI Assistant’s behavior and tone. They guide the assistant on how to respond, what context to remember, and what role it should play like being more casual, technical, concise, or strategic so it better matches your needs.">
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                <FiInfo />
                            </Box>
                        </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <TextField
                            placeholder="Add instructions for reference"
                            fullWidth
                            multiline
                            maxRows={6}
                            sx={{ fontSize: 16 }}
                            value={instructions}
                            onChange={e => {
                                setInstructions(e.target.value);
                                if (errors.instructions) setErrors(prev => ({ ...prev, instructions: '' }));
                            }}
                            error={!!errors.instructions}
                            helperText={errors.instructions}
                            required
                            onClick={() => {
                                eventsend('Instructions_clicked');
                                setInstructionsModalOpen(true)
                            }}
                            InputProps={{
                                endAdornment: (
                                    <Box sx={{ position: 'absolute', right: 10, bottom: 10, display: 'flex', alignItems: 'center' }}>
                                        <IconButton size="small" onClick={() => {
                                            setInstructionsModalOpen(true);
                                            eventsend("Instructions_clicked")
                                        }}>
                                            <FiExternalLink style={{ color: '#373FCC' }} />
                                        </IconButton>
                                    </Box>
                                )
                            }}
                            inputProps={{ style: { cursor: 'pointer' }, readOnly: true }}
                        />
                    </Box>
                    <InstructionsModal
                        open={isInstructionsModalOpen}
                        instructions={instructions}
                        setInstructions={setInstructions}
                        onClose={() => setInstructionsModalOpen(false)}
                        onViewReferences={() => { setInstructionsModalOpen(false); setReferencesModalOpen(true); }}
                    />
                    {isReferencesModalOpen && <ReferencesModal />}

                    <Divider sx={{ my: 2, borderColor: '#CCCCCC' }} />

                    <Typography fontWeight={"bold"} fontSize={13} mb={0.5}>Knowledge Base
                        <Tooltip title="Knowledge Base is a set of documents or information you provide to help the AI Assistant give accurate, contextual answers. It’s like giving your assistant a manual or FAQ so instead of generic replies, it references your specific data, processes, product details, or business rules while responding to users.">
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                <FiInfo />
                            </Box>
                        </Tooltip>
                    </Typography>
                    <Box display="flex" gap={2} mb={1}>
                        <CheerioButton
                            btnText="File Upload"
                            onclick={() => fileInputRef.current && fileInputRef.current.click()}
                            borderStyle={{ border: '2px solid #373FCC' }}
                            textStyle={{ color: '#373FCC', fontWeight: 500, fontSize: 18 }}
                            backColor={"#fff"}
                            disabled={isUploading}
                            icon={null}
                            id="file-upload-btn"
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.csv,.txt,.md,.ppt,.pptx,.xls,.xlsx,.txt"
                            style={{ display: 'none' }}
                            multiple
                            onChange={handleFileUpload}
                        />
                        <CheerioButton
                            btnText="Article links"
                            onclick={() => { setLinksModalOpen(true); setLinkInput(''); setEditLinkIdx(null); setLinkError(''); }}
                            borderStyle={{ border: '2px solid #373FCC' }}
                            textStyle={{ color: '#373FCC', fontWeight: 500, fontSize: 18 }}
                            backColor={"#fff"}
                            icon={null}
                            id="article-links-btn"
                        />
                    </Box>
                    <UploaderText type="document" textStyle={{ fontSize: 12, color: '#666', marginBottom: 2, marginTop: 2 }} />
                    {fileError && <Typography color="error" fontSize={12} mb={1}>{fileError}</Typography>}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 0, mr: 1 }}>
                                Knowledge Base (Files)
                            </Typography>
                            {errors.files && (
                                <Typography color="error" variant="caption">
                                    {errors.files}
                                </Typography>
                            )}
                        </Box>
                        {files.length === 0 ? (
                            <Typography fontSize={12} color="#888">No files uploaded</Typography>
                        ) : (
                            files.map((file) => (
                                <Box key={file.name} display="flex" alignItems="center" gap={1} mb={0.5} pl={1} pr={1} py={0.5} sx={{ background: '#F2F2F2', borderRadius: 1 }}>
                                    <img src={images.docPreview || images.pdfPreview} alt="doc" style={{ width: 20, height: 20, marginRight: 6 }} />
                                    <Typography fontSize={13} sx={{ flex: 1, fontWeight: 500 }}>{file.name}</Typography>
                                    {file.status === 'uploading' && (
                                        <Box sx={{ width: 60, mr: 1 }}>
                                            <Box sx={{ height: 6, bgcolor: '#E6E6E6', borderRadius: 3, overflow: 'hidden' }}>
                                                <Box sx={{ width: `${file.progress}%`, height: 6, bgcolor: '#373FCC', transition: 'width 0.2s' }} />
                                            </Box>
                                        </Box>
                                    )}
                                    <Tooltip title="Delete"><IconButton size="small" onClick={() => handleFileDelete(file)}><FiTrash2 color="#E50000" /></IconButton></Tooltip>
                                </Box>
                            ))
                        )}
                    </Box>
                    <Typography fontWeight={700} fontSize={14} mt={2} mb={0.5}>Links</Typography>
                    {links.length === 0 ? (
                        <Typography fontSize={12} color="#888" mb={1}>No links added</Typography>
                    ) : (
                        <Box mb={1}>
                            {links.map((link, idx) => (
                                <Box key={link.url + idx} display="flex" alignItems="center" gap={1} mb={0.5} pl={1} pr={1} py={0.5} sx={{ background: '#F2F2F2', borderRadius: 1 }}>
                                    <img src={images.LinkSel || images.Link || undefined} alt="link" style={{ width: 20, height: 20, marginRight: 6 }} />
                                    <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#6C47FF', fontSize: 14, fontWeight: 500, flex: 1, textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{link.url}</a>
                                    <Tooltip title="Edit"><IconButton size="small" onClick={() => { setLinksModalOpen(true); setEditLinkIdx(idx); setLinkInput(link.url); setLinkError(''); }}><FiEdit2 color="#373FCC" /></IconButton></Tooltip>
                                    <Tooltip title="Delete"><IconButton size="small" onClick={() => setLinks(links.filter((_, i) => i !== idx))}><FiTrash2 color="#E50000" /></IconButton></Tooltip>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {isLinksModalOpen && (
                        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ bgcolor: '#fff', borderRadius: 3, width: 520, p: 4, boxShadow: 3, position: 'relative', minHeight: 320 }}>
                                <IconButton size="small" sx={{ position: 'absolute', top: 16, right: 16 }} onClick={() => setLinksModalOpen(false)}>
                                    <span style={{ fontSize: 22, fontWeight: 700 }}>×</span>
                                </IconButton>
                                <Typography fontWeight={600} fontSize={15} mb={2} display="flex" alignItems="center" gap={1}>
                                    Article Links <Tooltip title="Add links to articles for your assistant."><FiInfo /></Tooltip>
                                </Typography>
                                {links.length === 0 && (
                                    <Typography fontSize={13} color="#888" mb={2}>No links added yet.</Typography>
                                )}
                                {links.map((link, idx) => (
                                    <Box key={idx} mb={1}>
                                        <Typography fontWeight={600} fontSize={13} mb={0.5}>{`Link ${idx + 1}`}</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={link.url}
                                            placeholder="https://"
                                            onChange={e => {
                                                const newLinks = [...links];
                                                newLinks[idx] = { url: e.target.value };
                                                setLinks(newLinks);
                                            }}
                                            sx={{ mb: 1 }}
                                        />
                                    </Box>
                                ))}
                                <Box display="flex" alignItems="center" gap={2} mt={2}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<img src={images.Link} alt="add" style={{ width: 20, height: 20 }} />}
                                        onClick={() => setLinks([...links, { url: '' }])}
                                        sx={{ borderRadius: 2, fontWeight: 500, textTransform: 'none', px: 2, borderColor: 'black', color: 'black' }}
                                    >
                                        Add link
                                    </Button>
                                    <Button
                                        variant="contained"
                                        disabled={links.length === 0 || links.some(l => !l.url.trim())}
                                        sx={{ borderRadius: 2, minWidth: 100, bgcolor: '#e6ddfa', color: '#373FCC', fontWeight: 600, textTransform: 'none', boxShadow: 'none', '&.Mui-disabled': { bgcolor: '#e6ddfa', color: '#373FCC' } }}
                                        onClick={() => setLinksModalOpen(false)}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                    <Divider />
                    <Typography fontWeight={500} fontSize={13} mt={2} mb={0.5}>Fallback Response
                        <Tooltip title="Fallback Response is the default message the AI Assistant sends when it doesn’t understand the user’s input or can’t find a matching answer. It acts as a safety net to keep the conversation going smoothly and ensures users aren’t left confused when the Assistant hits a dead end.">
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                <FiInfo />
                            </Box>
                        </Tooltip>
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Sorry, I didn’t understand what you said. Please try another question or I can redirect this chat to a human agent"
                        fullWidth
                        error={!!errors.fallbackResponse}
                        helperText={errors.fallbackResponse}
                        value={fallbackResponse}
                        onChange={e => {
                            setFallbackResponse(e.target.value)
                            eventsend('fallback_response_added');
                            if (errors.fallbackResponse) setErrors(prev => ({ ...prev, fallbackResponse: '' }));
                        }}
                    />

                    <Divider sx={{ mb: 1 }} />

                    <Box display="flex" gap={1} mt={2} mb={2} alignItems="center">
                        <Checkbox
                            checked={humanizeResponse}
                            onChange={e => {
                                setHumanizeResponse(e.target.checked);
                                eventsend('humanize_response_toggled', { enabled: e.target.checked });
                            }}
                            sx={{ p: 0, mr: 1 }}
                            color="secondary"
                        />
                        <Typography fontWeight={700} fontSize={14}>
                            Humanize AI Response
                        </Typography>
                        <Tooltip title="When enabled, the AI will break long messages into smaller chunks and simulate natural typing delays, making responses feel more human.">
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                <FiInfo />
                            </Box>
                        </Tooltip>
                    </Box>

                    {humanizeResponse && (
                        <Box sx={{ px: 2, mb: 3 }}>
                            <Typography fontSize={13} mb={1}>
                                Adjust Typing Speed (characters per second)
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Sorry, I didn’t understand what you said. Please try another question or I can redirect this chat to a human agent"
                                fullWidth
                                error={!!errors.fallbackResponse}
                                helperText={errors.fallbackResponse}
                                value={humanizeSpeed}
                                onChange={e => {
                                    setHumanizeSpeed(e.target.value)
                                    eventsend('humanize_speed_changed', { speed: e.target.value });
                                    if (errors.fallbackResponse) setErrors(prev => ({ ...prev, fallbackResponse: '' }));
                                }}
                            />
                        </Box>
                    )}

                    <Divider sx={{ mb: 1 }} />

                    <Box display="flex" gap={1} mt={2} mb={1}>
                        <Checkbox
                            checked={exitAfterTime}
                            onChange={e => {
                                setExitAfterTime(e.target.checked);
                                eventsend('exit_config_updated');
                            }}
                            sx={{ p: 0, mr: 1, alignSelf: 'center' }}
                            color="secondary"
                        />
                        <FormControl size="small" fullWidth disabled={!exitAfterTime}>
                            <InputLabel>Exit after time interval
                                <Tooltip title="Manage the chat duration effectively. You can establish a time limit for each conversation to ensure it remains focused and to the point. This approach will also help minimize costs associated with excessive chatting.">
                                    <Box component="span" sx={{ ml: 1, mr: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                        <FiInfo />
                                    </Box>
                                </Tooltip>
                            </InputLabel>
                            <Select
                                value={exitTimeValue}
                                label="Exit after time interval"
                                onChange={e => {
                                    setExitTimeValue(e.target.value);
                                    eventsend('exit_config_updated');
                                }}
                            >
                                <MenuItem value="3">3 minutes</MenuItem>
                                <MenuItem value="5">5 minutes</MenuItem>
                                <MenuItem value="10">10 minutes</MenuItem>
                                <MenuItem value="15">15 minutes</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box display="flex" gap={1} mb={1}>
                        <Checkbox
                            checked={exitAfterCount}
                            onChange={e => setExitAfterCount(e.target.checked)}
                            sx={{ p: 0, mr: 1, alignSelf: 'center' }}
                            color="secondary"
                        />
                        <FormControl size="small" fullWidth disabled={!exitAfterCount}>
                            <Typography fontWeight={500} fontSize={13} mt={0} mb={0.5}>Exit after no. of responses
                                <Tooltip title="Manage the number of responses before the chat ends. This helps keep the conversation concise and focused. You can set a limit to ensure the chat doesn’t drag on too long.">
                                    <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', color: '#373FCC', cursor: 'pointer' }}>
                                        <FiInfo />
                                    </Box>
                                </Tooltip>
                            </Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={exitCountValue}
                                onChange={(e) => setExitCountValue(e.target.value)}
                                inputProps={{ min: 1, max: 100 }}
                                disabled={!exitAfterCount}
                                fullWidth
                            />
                        </FormControl>

                    </Box>
                    <TextField size="small" placeholder="Exit Response" value={exitResponse} onChange={e => setExitResponse(e.target.value)} fullWidth sx={{ mb: 5 }} />
                </Box>

                {/* Right Panel: Chat Preview (sticky) */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fafbfc', height: '100vh', position: 'sticky', top: 0, right: 0, minWidth: 0 }}>
                    <Box sx={{ borderBottom: '1px solid #eee', p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70, background: '#fafbfc', zIndex: 3 }}>
                        <Typography fontWeight={800} fontSize={22}>Message Ground</Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Button
                                onClick={() => {
                                    eventsend('assistant_test_started');
                                    handleSubmit();
                                }}
                                disabled={submitLoading}
                                variant="contained" color="secondary" sx={{ textTransform: 'none', borderRadius: 2, background: '#373FCC', boxShadow: 'none', fontWeight: 700, fontSize: 16, px: 3 }}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                    {submitError && <Typography color="error" fontSize={13} mt={1}>{submitError}</Typography>}
                    <MessageGroundChatBox
                        agentId={agentId || ''}
                        authToken={authtoken}
                    />
                </Box>
            </Box>
        </>
    );
}
